import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveRestore, Loader2 } from 'lucide-react';

import { ListPagination } from '@/components/efs/ListPagination';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import { ApiClientError } from '@/lib/api-client';
import type { DeletedInboundLot } from './types';
import { useDeletedInboundLots, useRestoreDeletedInboundLot } from './useInbound';

interface DeletedInboundLotsDialogProps {
  batchId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canRestoreLinkedDispatch: boolean;
}

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const impactText = (row: DeletedInboundLot) => {
  const counts = row.impact_counts ?? {};
  return [
    counts.inbound_lots ?? 0,
    counts.inbound_packages ?? 0,
    counts.dismantling_sessions ?? 0,
    counts.dismantling_outputs ?? 0,
    counts.inventory_movements ?? 0,
  ].join(' / ');
};

export const DeletedInboundLotsDialog = ({
  batchId,
  open,
  onOpenChange,
  canRestoreLinkedDispatch,
}: DeletedInboundLotsDialogProps) => {
  const { t } = useTranslation('operations');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const list = useDeletedInboundLots({ monthly_batch_id: batchId, page, per_page: perPage }, open);
  const restore = useRestoreDeletedInboundLot();
  const resetRestore = restore.reset;
  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const meta = list.data?.meta;
  const total = meta?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageOutOfRange = page > totalPages;
  const restoreBlockers = useMemo(() => {
    if (!(restore.error instanceof ApiClientError)) return [];
    const raw = restore.error.details?.blockers;
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const blocker = item as { code?: unknown; count?: unknown };
      return typeof blocker.code === 'string' && typeof blocker.count === 'number'
        ? [{ code: blocker.code, count: blocker.count }]
        : [];
    });
  }, [restore.error]);

  useEffect(() => {
    if (!open) {
      setPage(1);
      resetRestore();
    }
  }, [open, resetRestore]);

  useEffect(() => {
    if (open && !list.isFetching && pageOutOfRange) {
      setPage(totalPages);
    }
  }, [list.isFetching, open, pageOutOfRange, totalPages]);

  return (
    <Dialog open={open} onOpenChange={restore.isPending ? undefined : onOpenChange}>
      <DialogContent className="max-h-[min(90vh,900px)] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.inbound.deleted_dialog.title', {
              defaultValue: '最近削除した入荷ロット',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.inbound.deleted_dialog.subtitle', {
              defaultValue:
                '削除済みの手動入荷ロットは30日間だけ復元できます。期限後は復元用データが削除されます。',
            })}
          </DialogDescription>
        </DialogHeader>

        {list.isLoading || list.isFetching || pageOutOfRange ? (
          <div className="flex items-center gap-2 rounded-md border border-efs-border p-4 text-sm text-efs-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('efs.operations.inbound.deleted_dialog.loading', {
              defaultValue: '削除済みデータを読み込んでいます…',
            })}
          </div>
        ) : list.error ? (
          <LocalizedApiError error={list.error} showTechnicalDetails={false} />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-efs-border p-6 text-center text-sm text-efs-text-muted">
            {t('efs.operations.inbound.deleted_dialog.empty', {
              defaultValue: '復元可能な削除済み入荷ロットはありません。',
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="overflow-hidden rounded-md border border-efs-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('efs.operations.inbound.deleted_dialog.columns.lot_code', {
                        defaultValue: '入荷コード',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.inbound.deleted_dialog.columns.deleted_at', {
                        defaultValue: '削除日時',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.inbound.deleted_dialog.columns.deleted_by', {
                        defaultValue: '削除者',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.inbound.deleted_dialog.columns.reason', {
                        defaultValue: '理由',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.inbound.deleted_dialog.columns.impact', {
                        defaultValue: '入庫/包装/解体/産出物/在庫',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.inbound.deleted_dialog.columns.deadline', {
                        defaultValue: '復元期限',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.inbound.deleted_dialog.columns.actions', {
                        defaultValue: '操作',
                      })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const superseded = !!row.superseded_at || !!row.replacement_inbound_lot_id;
                    const expired = !row.recoverable && !superseded;
                    const linkedDispatchPermissionBlocked =
                      row.return_step === 'dispatch' && !canRestoreLinkedDispatch;
                    const restoreDisabled = linkedDispatchPermissionBlocked || restore.isPending;
                    const restoreDisabledTitle = linkedDispatchPermissionBlocked
                      ? t(
                          'efs.operations.inbound.deleted_dialog.restore_dispatch_permission_hint',
                          {
                            defaultValue: '配車へ戻す権限がありません。',
                          },
                        )
                      : undefined;
                    return (
                      <TableRow key={row.archive_id}>
                        <TableCell className="font-mono text-xs">
                          <div>{row.lot_code || row.inbound_lot_id}</div>
                          <div className="mt-1 font-sans text-efs-text-muted">
                            {row.return_step === 'dispatch'
                              ? t('efs.operations.inbound.deleted_dialog.return_to_dispatch', {
                                  defaultValue: 'Returns to dispatch',
                                })
                              : t(
                                  'efs.operations.inbound.deleted_dialog.return_to_manual_creation',
                                  {
                                    defaultValue: 'Returns to before inbound creation',
                                  },
                                )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{formatDateTime(row.deleted_at)}</TableCell>
                        <TableCell className="max-w-[180px] text-xs">
                          <div>{row.deleted_by_display_name}</div>
                          <div className="truncate text-efs-text-muted">{row.deleted_by_email}</div>
                        </TableCell>
                        <TableCell className="max-w-[260px] text-xs">
                          <div className="line-clamp-3">{row.delete_reason}</div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{impactText(row)}</TableCell>
                        <TableCell className="text-xs">
                          <div>{formatDateTime(row.purge_after)}</div>
                          <div
                            className={
                              expired || superseded ? 'text-destructive' : 'text-efs-text-muted'
                            }
                          >
                            {superseded
                              ? t('efs.operations.inbound.deleted_dialog.superseded', {
                                  defaultValue: '再入荷で置き換え済み・復元不可',
                                })
                              : expired
                                ? t('efs.operations.inbound.deleted_dialog.expired', {
                                    defaultValue: '期限切れ',
                                  })
                                : t('efs.operations.inbound.deleted_dialog.days_left', {
                                    count: row.days_remaining,
                                    defaultValue: 'あと {{count}} 日',
                                  })}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {superseded || expired ? (
                            <span className="text-xs text-efs-text-muted">
                              {superseded
                                ? t(
                                    'efs.operations.inbound.deleted_dialog.restore_superseded_unavailable',
                                    {
                                      defaultValue: '再入荷で置き換え済みのため復元できません。',
                                    },
                                  )
                                : t('efs.operations.inbound.deleted_dialog.restore_unavailable', {
                                    defaultValue: '復元期限を過ぎているため復元できません。',
                                  })}
                            </span>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={restoreDisabled}
                              title={restoreDisabledTitle}
                              onClick={() => restore.mutate(row.archive_id)}
                            >
                              <ArchiveRestore className="mr-1 h-3 w-3" aria-hidden />
                              {t('efs.operations.inbound.deleted_dialog.restore', {
                                defaultValue: '復元',
                              })}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <ListPagination
              page={meta?.page ?? page}
              perPage={meta?.per_page ?? perPage}
              total={total}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPerPageChange={(nextPerPage) => {
                setPage(1);
                setPerPage(
                  PAGE_SIZE_OPTIONS.includes(nextPerPage) ? nextPerPage : DEFAULT_PER_PAGE,
                );
              }}
            />
          </div>
        )}

        {restore.error ? (
          <div className="space-y-2">
            <LocalizedApiError error={restore.error} showTechnicalDetails={false} />
            {restoreBlockers.length > 0 ? (
              <ul className="list-disc rounded-md border border-destructive/30 bg-destructive/5 py-2 pl-7 pr-3 text-sm text-destructive">
                {restoreBlockers.map((blocker) => (
                  <li key={blocker.code}>
                    {t(`efs.operations.inbound.lot_delete.blockers.${blocker.code}`, {
                      count: blocker.count,
                      defaultValue: `${blocker.code} (${blocker.count})`,
                    })}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default DeletedInboundLotsDialog;
